import { signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Errores de validación del backend: parseo del cuerpo de la respuesta y
 * marcado de los controles que los originaron.
 *
 * El `ValidationPipe` global del backend devuelve, además del `message` plano de
 * Nest, un arreglo `errores: { campo, mensaje }[]` con un ítem por constraint
 * incumplida (`backend/src/common/validation/validation-pipe.config.ts`). El
 * front lo aprovecha para pintar en rojo el campo culpable con su motivo, en vez
 * de mostrar un único mensaje genérico.
 *
 * Portado de `utils/errores-backend.ts` del backoffice. Se deja afuera lo que
 * sólo tiene sentido allá: la opción `controlesSinMensaje`, los mensajes por
 * sub-grupo (`mensajesDeGrupo`) y todo lo que existía para alimentar el toast —
 * el portal no tiene toasts, así que los mensajes que no entran bajo un campo
 * los muestra el alerta de arriba del formulario.
 */

/** Un error de validación apuntando al campo que lo originó. */
export interface ErrorCampoBackend {
  campo: string;
  mensaje: string;
}

export interface ErrorBackendParseado {
  /** Un mensaje por error. */
  mensajes: string[];
  /** Los errores que el backend asoció a un campo concreto. */
  campos: ErrorCampoBackend[];
}

/** Mensaje que arma Angular cuando la respuesta no trae cuerpo útil. */
const RE_FALLA_HTTP = /^Http failure response/i;

/** Claves donde puede venir el arreglo de errores por campo, en orden de preferencia. */
const CLAVES_ERRORES = ['errores', 'errors', 'message', 'error'] as const;

/**
 * Interpreta el cuerpo de error de una respuesta fallida.
 *
 * Es tolerante a formatos porque no todos los endpoints pasan por el
 * `ValidationPipe`: acepta `errores: { campo, mensaje }[]`, `message: string[]`
 * (un mensaje por error) y `message: string`.
 *
 * @param respaldo mensaje a usar cuando la respuesta no trae ninguno legible.
 */
export function parsearErrorBackend(
  err: unknown,
  respaldo: string,
): ErrorBackendParseado {
  const cuerpo = cuerpoDeError(err);

  const campos = leerErroresDeCampo(cuerpo);
  if (campos.length) return { mensajes: campos.map((e) => e.mensaje), campos };

  // Un 5xx o una caída de red no traen copy para el usuario ('Internal server
  // error'): ahí manda el respaldo, que sí sabe qué se estaba intentando hacer.
  const mensajes = esFallaDeServidor(err) ? [] : leerMensajes(cuerpo);
  if (mensajes.length) return { mensajes, campos };
  return { mensajes: respaldo ? [respaldo] : [], campos };
}

/**
 * `status` 0 es red/CORS y 5xx es una falla interna: si el cuerpo trae
 * `message`, es texto técnico de Nest y no algo que el usuario pueda accionar.
 * Sin `status` numérico (un error que no es HTTP) se asume que el mensaje sirve.
 */
function esFallaDeServidor(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' && (status === 0 || status >= 500);
}

function cuerpoDeError(err: unknown): Record<string, unknown> {
  const cuerpo = (err as { error?: unknown } | null)?.error ?? err;
  return cuerpo && typeof cuerpo === 'object'
    ? (cuerpo as Record<string, unknown>)
    : {};
}

function leerErroresDeCampo(
  cuerpo: Record<string, unknown>,
): ErrorCampoBackend[] {
  const arreglo = CLAVES_ERRORES.map((clave) => cuerpo[clave]).find(
    Array.isArray,
  ) as unknown[] | undefined;

  return (arreglo ?? [])
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const o = item as Record<string, unknown>;
      const campo = o['campo'] ?? o['field'] ?? o['property'];
      const mensaje = o['mensaje'] ?? o['message'] ?? o['msg'];
      return typeof campo === 'string' && typeof mensaje === 'string'
        ? { campo, mensaje }
        : null;
    })
    .filter((error): error is ErrorCampoBackend => error !== null);
}

function leerMensajes(cuerpo: Record<string, unknown>): string[] {
  const crudo = cuerpo['message'] ?? cuerpo['mensaje'];
  const candidatos = Array.isArray(crudo) ? crudo : [crudo];
  return candidatos.filter(
    (m): m is string =>
      typeof m === 'string' && !!m.trim() && !RE_FALLA_HTTP.test(m),
  );
}

/**
 * Errores de backend vigentes sobre un formulario.
 *
 * Marca cada control rechazado con el error `backend` —lo que ya alcanza para
 * que el campo se pinte en rojo— y expone su mensaje para el texto de ayuda. Al
 * editar el campo, Angular revalida y el error desaparece solo.
 *
 * ```ts
 * private readonly erroresBackend = new ErroresBackendForm(this.form, BACKEND_A_CONTROL);
 * // ...
 * const { mensajes, campos } = parsearErrorBackend(err, 'No se pudo enviar.');
 * // `aplicar` devuelve sólo lo que NO quedó visible bajo un campo.
 * const sobrantes = this.erroresBackend.aplicar(campos);
 * ```
 */
export class ErroresBackendForm {
  private readonly _mensajes = signal<Record<string, string>>({});
  private suscripcion?: Subscription;

  /** Mensaje vigente por nombre de control. */
  readonly mensajes = this._mensajes.asReadonly();

  /**
   * @param form formulario a marcar.
   * @param mapa traducción `campo del backend` → `nombre del control`. Los
   *             campos que no figuran se buscan por su propio nombre.
   */
  constructor(
    private readonly form: FormGroup,
    private readonly mapa: Record<string, string> = {},
  ) {}

  /**
   * Marca en rojo cada campo rechazado con su mensaje, descartando antes los
   * errores del intento anterior.
   *
   * @returns los mensajes que NO quedaron visibles bajo un campo y que, por lo
   *          tanto, hay que mostrar aparte. Repetir arriba un texto que ya se lee
   *          debajo del campo es ruido, así que esos se omiten.
   */
  aplicar(campos: readonly ErrorCampoBackend[]): string[] {
    this.limpiar();

    const sinControl: string[] = [];
    const porControl = new Map<string, string>();
    for (const error of campos) {
      const nombre = this.mapa[error.campo] ?? error.campo;
      const control = this.form.get(nombre);
      // Un control deshabilitado ignora `setErrors`: el campo no puede mostrar
      // el mensaje, así que va arriba.
      if (!control || control.disabled) {
        sinControl.push(error.mensaje);
        continue;
      }
      // Bajo el campo entra un solo mensaje: si incumple varias constraints, la
      // primera se muestra ahí y el resto sale arriba.
      if (porControl.has(nombre)) {
        sinControl.push(error.mensaje);
        continue;
      }
      porControl.set(nombre, error.mensaje);
    }

    for (const [nombre, mensaje] of porControl) {
      const control = this.form.get(nombre)!;
      control.markAsTouched();
      // Los campos pintan el rojo con `invalid && dirty`, no con `touched`.
      control.markAsDirty();
      control.setErrors({ ...(control.errors ?? {}), backend: mensaje });
    }

    if (!porControl.size) return sinControl;

    this._mensajes.set(Object.fromEntries(porControl));
    // Editar el campo revalida el control y borra su error `backend`; acá sólo
    // queda soltar el mensaje que quedó colgado del texto de ayuda. La
    // suscripción observa el `valueChanges` del propio form, así que muere con
    // él (y con el componente que lo declara).
    this.suscripcion = this.form.valueChanges.subscribe(() =>
      this.sincronizar(),
    );
    return sinControl;
  }

  /** Descarta los errores vigentes: cada control marcado pierde su `backend`. */
  limpiar(): void {
    this.suscripcion?.unsubscribe();
    this.suscripcion = undefined;

    for (const nombre of Object.keys(this._mensajes())) {
      const control = this.form.get(nombre);
      if (!control?.hasError('backend')) continue;
      const resto = { ...control.errors };
      delete resto['backend'];
      control.setErrors(Object.keys(resto).length ? resto : null);
    }
    this._mensajes.set({});
  }

  /** Mensaje que el backend rechazó para ese control, si sigue vigente. */
  mensajeDe(nombre: string): string | undefined {
    return this._mensajes()[nombre];
  }

  private sincronizar(): void {
    const vigentes = this._mensajes();
    const restantes: Record<string, string> = {};
    for (const [nombre, mensaje] of Object.entries(vigentes)) {
      if (this.form.get(nombre)?.hasError('backend')) restantes[nombre] = mensaje;
    }
    if (Object.keys(restantes).length === Object.keys(vigentes).length) return;

    this._mensajes.set(restantes);
    if (!Object.keys(restantes).length) {
      this.suscripcion?.unsubscribe();
      this.suscripcion = undefined;
    }
  }
}
